"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Loader2, ShoppingBag } from "lucide-react"
import { shopifyPeriodToDateRange } from "@/lib/date-utils"
import { useShopifyAttributionSettings } from "@/hooks/use-tenant-settings"
import { useAuth } from "@/hooks/use-auth"
import { usePermissions } from "@/hooks/use-permissions"
import { SHOPIFY_PERMISSIONS } from "@/lib/auth/permissions.constants"
import { useShopify } from "@/hooks/use-shopify"
import { DEFAULT_SHOPIFY_PERIOD, type ShopifyPeriod } from "@/components/shopify/attribution-dashboard/period-constants"
import { CrossSellContent } from "./cross-sell-content"

export function CrossSellPage() {
  const { user } = useAuth()
  const { activeIntegrations, isLoading } = useShopify()
  const [selectedIntegration, setSelectedIntegration] = useState<string>("")
  const { can } = usePermissions()

  const userTimezone = useMemo(() => {
    if (!user) return "UTC"
    return typeof user.timezone === "string" && user.timezone.trim() !== "" ? user.timezone : "UTC"
  }, [user])

  const [period, setPeriod] = useState<ShopifyPeriod>(DEFAULT_SHOPIFY_PERIOD)
  const [customRange, setCustomRange] = useState<{ from: string; to: string } | null>(null)

  const dateRange = useMemo(
    () => ({
      ...shopifyPeriodToDateRange(period, userTimezone, customRange ?? undefined),
      timezone: userTimezone,
    }),
    [period, customRange, userTimezone],
  )

  const handlePeriodChange = useCallback(
    (newPeriod: ShopifyPeriod, newCustomRange?: { from: string; to: string }) => {
      setPeriod(newPeriod)
      setCustomRange(newCustomRange ?? null)
    },
    [],
  )

  const {
    settings: shopifyAttributionSettings,
    loading: isSettingsLoading,
  } = useShopifyAttributionSettings()

  // Auto-select first active integration
  useEffect(() => {
    if (activeIntegrations.length > 0 && !selectedIntegration) {
      setSelectedIntegration(activeIntegrations[0].id)
    }
  }, [activeIntegrations, selectedIntegration])

  if (!can(SHOPIFY_PERMISSIONS.VIEW)) {
    return (
      <div className="p-6 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert>
              <AlertDescription>
                You don&apos;t have permission to access Cross-Sell Analysis. Contact your administrator.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (isLoading && activeIntegrations.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2">Loading...</span>
      </div>
    )
  }

  if (activeIntegrations.length === 0) {
    return (
      <div className="p-6 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Cross-Sell Analysis</CardTitle>
            <CardDescription>Cross-sell and bundle analysis</CardDescription>
          </CardHeader>
          <CardContent>
            <Alert>
              <ShoppingBag className="h-4 w-4" />
              <AlertDescription>
                No active Shopify integrations found. Please connect and enable a Shopify store from Integrations settings.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Cross-Sell Analysis</h1>
        <p className="text-sm text-muted-foreground">
          Cross-sell pairs, focus product analysis, and bundle builder
        </p>
      </div>
      {selectedIntegration && user && !isSettingsLoading && (
        <CrossSellContent
          integrationId={selectedIntegration}
          dateRange={dateRange}
          period={period}
          customRange={customRange}
          onPeriodChange={handlePeriodChange}
          minimumOrderTotal={shopifyAttributionSettings.minimum_order_total ?? 0}
          stores={activeIntegrations.map((i) => ({ id: i.id, shop_domain: i.shop_domain }))}
          selectedStore={selectedIntegration}
          onStoreChange={setSelectedIntegration}
        />
      )}
    </div>
  )
}
